import { Box, Typography } from '@mui/material'
import { Table } from 'antd'
import { forwardRef, useMemo } from 'react'

import {
  IProductDailyTotalReport,
} from '../../../../../types/report'
import { numberFormat } from '../../../../../utils'
import { Tag } from '../../../../../components/Tag'
import { useUser } from '../../../../../hooks/useUser'

interface IIndividualDisplayItemsTable {
  data?: IProductDailyTotalReport[]
}

/**
 * แปลงข้อมูล: เพิ่มแถวสินค้าที่มาจากเซ็ตเท่านั้น (ไม่มีสินค้าปกติมาจับคู่)
 * กรณี: สินค้าลูกใน collections ของเซ็ต ไม่มี productCode ตรงกับสินค้าปกติใดๆ
 * แสดงเป็นแถวใหม่: quantity=0, quantityPack=sum, quantityTotal=sum
 */
function transformDataWithExtractedFromSets(
  data: IProductDailyTotalReport[],
): IProductDailyTotalReport[] {
  if (!data?.length) return data ?? []

  const normalProductCodes = new Set(
    data
      .filter((item) => !item.isCollection)
      .map((item) => item.productCode),
  )

  type ExtractedItem = {
    productCode: string
    productName: string
    quantityPack: number
    cost: number
    codTotal: number
    bankTotal: number
  }

  const fromSetOnlyMap = new Map<string, ExtractedItem>()

  data
    .filter((item) => item.isCollection && item.collections?.length)
    .forEach((setItem) => {
      const collections = setItem.collections!
      const totalQtyInSet = collections.reduce((sum, c) => sum + c.quantity, 0)

      collections.forEach((col) => {
        const key = col.productCode
        const proportion = totalQtyInSet > 0 ? col.quantity / totalQtyInSet : 0
        const costPart = col.cost ?? 0
        const codPart = setItem.codTotal * proportion
        const bankPart = setItem.bankTotal * proportion

        const current = fromSetOnlyMap.get(key)
        if (current) {
          current.quantityPack += col.quantity
          current.cost += costPart
          current.codTotal += codPart
          current.bankTotal += bankPart
        } else {
          fromSetOnlyMap.set(key, {
            productCode: col.productCode,
            productName: col.productName,
            quantityPack: col.quantity,
            cost: costPart,
            codTotal: codPart,
            bankTotal: bankPart,
          })
        }
      })
    })

  const extractedRows: (IProductDailyTotalReport & { cost?: number })[] = []

  const mergedData = data.map((row) => {
    if (row.isCollection) return row
    const setPortion = fromSetOnlyMap.get(row.productCode)
    if (!setPortion) return row
    return {
      ...row,
      cost: (row.cost ?? 0) + setPortion.cost,
      codTotal: row.codTotal + setPortion.codTotal,
      bankTotal: row.bankTotal + setPortion.bankTotal,
    }
  })

  fromSetOnlyMap.forEach((val, productCode) => {
    if (!normalProductCodes.has(productCode)) {
      extractedRows.push({
        productCode: val.productCode,
        productName: val.productName,
        categoryName: '',
        product: '',
        productImageUrl: null,
        quantity: 0,
        quantityPack: val.quantityPack,
        quantityTotal: val.quantityPack,
        codTotal: val.codTotal,
        bankTotal: val.bankTotal,
        cost: val.cost,
      })
    }
  })

  return [...mergedData, ...extractedRows]
}

const IndividualDisplayItemsTable = forwardRef<
  HTMLTableElement,
  IIndividualDisplayItemsTable
>((props: IIndividualDisplayItemsTable, ref) => {
  const { data } = props
  const { user } = useUser()

  const tableData = useMemo(
    () => transformDataWithExtractedFromSets(data ?? []),
    [data],
  )

  const expandedRowRender = (
    record: IProductDailyTotalReport & { key: number },
  ) => {
    const columns = [
      {
        title: 'รหัสสินค้า',
        dataIndex: 'productCode',
        key: 'productCode',
        with: '60',
        align: 'center' as const,
        render: (productCode: string) => (
          <Typography variant="subtitle2" color="text.secondary">
            {productCode}
          </Typography>
        ),
      },
      {
        title: 'ชื่อสินค้า',
        dataIndex: 'productName',
        key: 'productName',

        align: 'left' as const,
        render: (productName: string) => (
          <Typography variant="subtitle2" color="text.secondary">
            {productName}
          </Typography>
        ),
      },
      {
        title: 'จำนวน',
        dataIndex: 'quantity',
        key: 'quantity',
        with: '120',
        align: 'center' as const,
        render: (quantity: string) => (
          <Typography variant="subtitle2" color="text.secondary">
            {quantity}
          </Typography>
        ),
      },
      {
        title: 'ต้นทุนสินค้า',
        dataIndex: 'cost',
        key: 'cost',
        align: 'center' as const,
        render: (cost: number | undefined) => (
          <Typography variant="subtitle2" color="text.secondary">
            {user?.isNotAllowSeeCost ? '-' : numberFormat(cost ?? 0, 2)}
          </Typography>
        ),
      },
    ]
    return (
      <Table
        columns={columns}
        scroll={{
          x: columns.reduce(
            (pre, cur: any) => (cur.width ? cur.width + pre : pre),
            0,
          ),
        }}
        dataSource={record.collections}
        pagination={false}
      />
    )
  }

  const columns = [
    {
      title: 'รหัสสินค้า',
      dataIndex: 'productCode',
      key: 'productCode',
      with: '60',
      align: 'center' as const,
      render: (productCode: string) => (
        <Typography variant="subtitle2" color="text.secondary">
          {productCode}
        </Typography>
      ),
    },
    {
      title: 'ชื่อสินค้า',
      dataIndex: 'productName',
      key: 'productName',
      width: '40%',
      align: 'left' as const,
      render: (productName: string, item: IProductDailyTotalReport) => {
        return (
          <Box display={'flex'} alignItems={'center'} gap={1}>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              textAlign={'left'}
            >
              {productName}
            </Typography>
            {item.isCollection && <Tag color="error">สินค้าแบบเซ็ต</Tag>}
          </Box>
        )
      },
    },
    {
      title: 'ขนาด',
      dataIndex: 'size',
      key: 'size',
      align: 'center' as const,
      render: (_: string, item: IProductDailyTotalReport) => {
        return (
          <Typography
            variant="subtitle2"
            color="text.secondary"
            textAlign={'center'}
          >
            {item?.meta?.size ?? '-'}
          </Typography>
        )
      },
    },
    {
      title: 'น้ำหนัก',
      dataIndex: 'weight',
      key: 'weight',
      align: 'center' as const,
      render: (_: string, item: IProductDailyTotalReport) => {
        return (
          <Typography
            variant="subtitle2"
            color="text.secondary"
            textAlign={'center'}
          >
            {item?.meta?.weight ?? '-'}
          </Typography>
        )
      },
    },
    {
      title: 'สี',
      dataIndex: 'color',
      key: 'color',
      align: 'center' as const,
      render: (_: string, item: IProductDailyTotalReport) => {
        return (
          <Typography
            variant="subtitle2"
            color="text.secondary"
            textAlign={'center'}
          >
            {item?.meta?.color ?? '-'}
          </Typography>
        )
      },
    },
    {
      title: 'จำนวน',
      dataIndex: 'quantity',
      key: 'quantity',
      align: 'center' as const,
      render: (quantity: number) => (
        <Typography
          variant="subtitle2"
          color="text.secondary"
          textAlign={'center'}
        >
          {numberFormat(quantity)}
        </Typography>
      ),
    },
    {
      title: 'จำนวน (จากสินค้าแบบเซ็ต)',
      dataIndex: 'quantityPack',
      key: 'quantityPack',
      align: 'center' as const,
      render: (quantityPack: number) => (
        <Typography
          variant="subtitle2"
          color="text.secondary"
          textAlign={'center'}
        >
          {numberFormat(quantityPack)}
        </Typography>
      ),
    },
    {
      title: 'จำนวนทั้งหมด',
      dataIndex: 'quantityTotal',
      key: 'quantityTotal',
      align: 'center' as const,
      render: (quantityTotal: number) => (
        <Typography
          variant="subtitle2"
          color="text.secondary"
          textAlign={'center'}
        >
          {numberFormat(quantityTotal)}
        </Typography>
      ),
    },
    {
      title: 'ต้นทุนสินค้า',
      dataIndex: 'cost',
      key: 'cost',
      align: 'center' as const,
      render: (cost: number | undefined) => (
        <Typography
          variant="subtitle2"
          color="text.secondary"
          textAlign={'center'}
        >
          {user?.isNotAllowSeeCost ? '-' : numberFormat(cost ?? 0, 2)}
        </Typography>
      ),
    },
    {
      title: 'COD',
      dataIndex: 'codTotal',
      key: 'codTotal',
      align: 'center' as const,
      render: (codTotal: number) => (
        <Typography
          variant="subtitle2"
          color="text.secondary"
          textAlign={'center'}
        >
          {numberFormat(codTotal, 2)}
        </Typography>
      ),
    },
    {
      title: 'BANK',
      dataIndex: 'bankTotal',
      key: 'bankTotal',
      align: 'center' as const,
      render: (bankTotal: number) => (
        <Typography
          variant="subtitle2"
          color="text.secondary"
          textAlign={'center'}
        >
          {numberFormat(bankTotal, 2)}
        </Typography>
      ),
    },
    {
      title: 'รวม',
      dataIndex: 'total',
      key: 'total',
      align: 'center' as const,
      render: (_: undefined, dailySalesItem: IProductDailyTotalReport) => (
        <Typography
          variant="subtitle2"
          color="text.secondary"
          textAlign={'center'}
        >
          {numberFormat(dailySalesItem.codTotal + dailySalesItem.bankTotal, 2)}
        </Typography>
      ),
    },
  ]

  return (
    <div ref={ref}>
      <Table
        size={'middle'}
        columns={columns}
        expandable={{
          expandedRowRender: expandedRowRender as any,
          rowExpandable: (record) => record?.isCollection ?? false,
        }}
        dataSource={tableData.map(
          (dailySalesItem: IProductDailyTotalReport, index: number) => ({
            ...dailySalesItem,
            key: index,
          }),
        )}
        pagination={false}
        bordered
      ></Table>
    </div>
  )
})

export default IndividualDisplayItemsTable
